import { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingFinishedExecutionsLoading,
  selectCrossClientLandingRunningExecutionsLoading,
  selectCrossClientLandingDecompositionResult,
  selectCrossClientInitLoading,
  selectCrossClientEvaluateLoading,
} from '../../../store/crossClient/crossClientLanding'
import {
  type CrossClientQueryClauseResolverMapping,
  type CrossClientQueryExecution,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
} from '../../../models/CrossClient'
import { CrossClientLandingFooter } from './Footer/CrossClientLandingFooter'
import { CrossClientLandingInitialization } from './CrossClientLandingInitialization'
import { CrossClientLimit } from './Limit/CrossClientLimit'
import { useCrossClientRemainingQueries } from './Limit/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { type PredefinedCrossClientQuery } from '../Predefined/crossClientPredefinedQueries'
import { useCrossClientQueryLandingExecution } from './hooks'
import { useCrossClientQuerySettings } from './crossClientSettings'
import { CrossClientQueryInput, MAX_USER_QUESTION_LENGTH } from './CrossClientQueryInput'
import { CrossClientChatReview, CrossClientChatReviewActions } from './CrossClientChatReview'
import { CrossClientQueryError } from './CrossClientQueryError'
import { CrossClientClientsPill } from './Pills/CrossClientClientsPill'
import { CrossClientSourcesPill } from './Pills/CrossClientSourcesPill'
import './CrossClientLanding.scss'

export const CrossClientLanding = () => {
  const { tracked, trackingClient } = useTracking()

  const { state: initialPredefinedExecutionState } = useLocation()
  const navigate = useNavigate()

  const finishedExecutionsLoading = useAppSelector(selectCrossClientLandingFinishedExecutionsLoading)
  const runningExecutionsLoading = useAppSelector(selectCrossClientLandingRunningExecutionsLoading)
  const decompositionResult = useAppSelector(selectCrossClientLandingDecompositionResult)
  const initLoading = useAppSelector(selectCrossClientInitLoading)
  const evaluateLoading = useAppSelector(selectCrossClientEvaluateLoading)

  const { remainingCcQueriesPerWeek } = useCrossClientRemainingQueries()

  const [userQuestion, setUserQuestion] = useState<string>(() => initialPredefinedExecutionState?.userQuestion ?? '')
  const [wasPredefined, setWasPredefined] = useState<boolean>(
    () => !!(initialPredefinedExecutionState?.userQuestion && initialPredefinedExecutionState?.decomposition),
  )
  const { sources, setSources, clientQueryMode, setClientQueryMode } = useCrossClientQuerySettings()

  const { initError, reviewState, decomposeCrossClientQuery, finalizeExecution, resetExecution, discardExecution } = useCrossClientQueryLandingExecution()

  const atomicClauses = decompositionResult?.decomposition?.atomicClauses ?? []
  const numberOfClauses = atomicClauses.length
  const cannotExecuteNew = !remainingCcQueriesPerWeek
  const buttonDisabled = cannotExecuteNew || userQuestion.trim().length < 3 || userQuestion.trim().length > MAX_USER_QUESTION_LENGTH
  const isWorking = initLoading
  const isReviewing = !isWorking && (!!reviewState || numberOfClauses > 0)
  const inputMode = isWorking ? 'working' : isReviewing ? 'review' : 'input'
  const chatCardStateClass = isWorking
    ? 'cross-client-chat-card--working'
    : isReviewing
      ? 'cross-client-chat-card--review'
      : cannotExecuteNew
        ? 'cross-client-chat-card--disabled'
        : userQuestion.trim().length > 0
          ? 'cross-client-chat-card--typed'
          : ''
  const showBackdrop = isWorking || isReviewing

  const triggerPredefinedDecompose = useCallback(
    async (args: {
      userQuestion: string
      decomposition: CrossClientQueryLogicalQuestionDecomposition
      nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
      clauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
    }) => {
      setUserQuestion(args.userQuestion)
      setWasPredefined(true)
      navigate('/cross-client', { state: { queryInProgress: true } })
      await decomposeCrossClientQuery({
        userQuestion: args.userQuestion,
        sources,
        clientQueryMode,
        decomposition: args.decomposition,
        nnfDecomposition: args.nnfDecomposition,
        clauseResolverMapping: args.clauseResolverMapping ?? undefined,
      })
    },
    [decomposeCrossClientQuery, navigate, sources, clientQueryMode],
  )

  const handleConfirm = tracked(
    async () => {
      if (decompositionResult) {
        const resultSummary = reviewState?.kind === 'instant-no-matches' ? reviewState.resultSummary : undefined
        await finalizeExecution(decompositionResult.executionId, resultSummary)
      }
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_CONFIRMED,
      eventDetails: {
        type: wasPredefined ? 'pre-defined' : 'custom',
        query: userQuestion,
        sharedMode: clientQueryMode,
      },
    },
  )

  const restart = useCallback(() => {
    resetExecution()
    setUserQuestion('')
    setWasPredefined(false)
    navigate('/cross-client', { replace: true, state: null })
  }, [resetExecution, navigate])

  const handleRestart = tracked(
    () => {
      discardExecution()
      setUserQuestion('')
      setWasPredefined(false)
      navigate('/cross-client', { replace: true, state: null })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_DENIED,
      eventDetails: { type: wasPredefined ? 'pre-defined' : 'custom', query: userQuestion },
    },
  )

  const handleInitializeCustomCrossClientQuery = tracked(
    async () => {
      setWasPredefined(false)
      navigate('/cross-client', { state: { queryInProgress: true } })
      await decomposeCrossClientQuery({
        userQuestion,
        sources,
        clientQueryMode,
      })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'custom',
        query: userQuestion,
        sources: sources.map((source) => (source.type === 'integration' ? source.integrationType : source.type)),
      },
    },
  )

  const handlePredefinedCrossClientQuerySelected = tracked(
    async (predefinedQuery: PredefinedCrossClientQuery) => {
      await triggerPredefinedDecompose({
        userQuestion: predefinedQuery.description,
        decomposition: predefinedQuery.decomposition,
        nnfDecomposition: predefinedQuery.nnfDecomposition,
        clauseResolverMapping: predefinedQuery.clauseResolverMapping,
      })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (predefinedQuery: PredefinedCrossClientQuery) => {
        return { query: predefinedQuery.description }
      },
    },
  )

  const handleExecutionRerun = tracked(
    async (selectedExecution: CrossClientQueryExecution) => {
      const { userQuestion: rerunQuestion, decomposition, nnfDecomposition } = selectedExecution
      if (!decomposition) {
        return
      }
      await triggerPredefinedDecompose({
        userQuestion: rerunQuestion,
        decomposition,
        nnfDecomposition,
        clauseResolverMapping: undefined,
      })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_RERUN,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (selectedExecution: CrossClientQueryExecution) => {
        return { executionId: selectedExecution.id }
      },
    },
  )

  useEffect(() => {
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_OPENED,
    })
  }, [trackingClient])

  useEffect(() => {
    if (!initialPredefinedExecutionState?.queryInProgress && decompositionResult) {
      restart()
    }
  }, [initialPredefinedExecutionState?.queryInProgress, decompositionResult, restart])

  const didInitFromLocationStateRef = useRef(false)
  useEffect(() => {
    if (didInitFromLocationStateRef.current) return
    if (!initialPredefinedExecutionState?.userQuestion || !initialPredefinedExecutionState?.decomposition) return
    didInitFromLocationStateRef.current = true
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time mount-init; guarded by didInitFromLocationStateRef so re-runs (e.g., when triggerPredefinedDecompose's identity changes after settings finish loading) are no-ops.
    void triggerPredefinedDecompose({
      userQuestion: initialPredefinedExecutionState.userQuestion,
      decomposition: initialPredefinedExecutionState.decomposition,
      nnfDecomposition: initialPredefinedExecutionState.nnfDecomposition,
      clauseResolverMapping: undefined,
    })
  }, [initialPredefinedExecutionState, triggerPredefinedDecompose])

  return (
    <>
      <CrossClientLandingInitialization />
      <div className='truly-full-size-content d-flex flex-column align-items-center position-relative cross-client-landing-container'>
        <div className='d-flex flex-column gap-3 align-items-center zocks-mb-4'>
          <div className='paragraph-small zocks-mt-4 py-1 zocks-px-0.75 text-uppercase zocks-border-1.0 medium zocks-text-gray-700 cross-client-landing-header'>
            Client Queries
          </div>
          <div className='d-flex flex-column gap-2 text-center'>
            <h2 className='medium'>Find the right clients for every opportunity</h2>
            <div className='paragraph-medium'>Ask questions in plain language. Your AI Assistant delivers results in seconds.</div>
          </div>
        </div>
        {initError ? (
          <div className='d-flex flex-column w-100 align-items-center'>
            <CrossClientQueryError onRestart={handleRestart} />
          </div>
        ) : null}
        {!finishedExecutionsLoading && !runningExecutionsLoading ? (
          <div className='d-flex flex-column align-items-center zocks-gap-2 w-100 mb-4'>
            <CrossClientLandingFooter
              runDisabled={cannotExecuteNew}
              onPredefinedQuerySelect={handlePredefinedCrossClientQuerySelected}
              onRerun={handleExecutionRerun}
            />
          </div>
        ) : null}
        {showBackdrop ? <div className='position-absolute top-0 start-0 end-0 bottom-0 z-1 cross-client-landing-backdrop' /> : null}
        <div className='cross-client-landing-dock position-sticky bottom-0 w-100 z-2 d-flex flex-column align-items-center mt-auto gap-2 px-3'>
          <div className={classNames('cross-client-chat-card', chatCardStateClass)}>
            <CrossClientQueryInput
              userQuestion={userQuestion}
              onQuestionChange={setUserQuestion}
              onSubmit={handleInitializeCustomCrossClientQuery}
              disabled={cannotExecuteNew || initLoading || !!decompositionResult}
              submitDisabled={buttonDisabled}
              isLoading={initLoading}
              hasExecution={!!decompositionResult}
              mode={inputMode}
              reviewContent={isReviewing && reviewState ? <CrossClientChatReview reviewState={reviewState} /> : null}
              footerRight={
                isReviewing && reviewState ? (
                  <CrossClientChatReviewActions reviewState={reviewState} onConfirm={handleConfirm} onRestart={handleRestart} isLoading={evaluateLoading} />
                ) : null
              }
              footerLeft={
                <>
                  <CrossClientClientsPill
                    mode={clientQueryMode}
                    onChange={setClientQueryMode}
                    disabled={cannotExecuteNew || initLoading || !!decompositionResult || evaluateLoading}
                  />
                  <CrossClientSourcesPill
                    sources={sources}
                    onChange={setSources}
                    disabled={cannotExecuteNew || initLoading || !!decompositionResult || evaluateLoading}
                  />
                </>
              }
            />
          </div>
          <CrossClientLimit />
        </div>
      </div>
    </>
  )
}
